// Item Delivery Service — reads and writes delivery records to Supabase.

import { getSupabaseClient } from './supabaseClient';
import type { ItemDelivery, PredictedScores, DeliveryType } from '../types/delivery';
import { computeCalibrationStats } from '../../supabase/functions/_shared/calibrationCore.ts';

function rowToDelivery(row: Record<string, unknown>): ItemDelivery {
  const str = (v: unknown): string | null => (v == null ? null : String(v));
  return {
    id: String(row.id),
    itemId: String(row.item_id),
    workspaceId: String(row.workspace_id),
    deliveredAt: String(row.delivered_at),
    deliveryType: row.delivery_type as DeliveryType,
    predictedScores: (row.predicted_scores as ItemDelivery['predictedScores']) ?? null,
    actualMetrics: (row.actual_metrics as ItemDelivery['actualMetrics']) ?? null,
    scoreAccuracy: row.score_accuracy != null ? Number(row.score_accuracy) : null,
    notes: str(row.notes),
    lessonsLearned: str(row.lessons_learned),
    createdBy: str(row.created_by),
    createdAt: String(row.created_at),
  };
}

export async function createItemDelivery(input: {
  itemId: string;
  workspaceId: string;
  deliveryType: DeliveryType;
  predictedScores?: PredictedScores | null;
  actualMetrics?: Record<string, number | string> | null;
  notes?: string;
  lessonsLearned?: string;
  createdBy: string;
}): Promise<ItemDelivery> {
  const supabase = getSupabaseClient();

  // Compute a simple accuracy ratio when both predicted score and actual score exist.
  let scoreAccuracy: number | null = null;
  const predictedScore = input.predictedScores?.numericScore;
  const actualScore = typeof input.actualMetrics?.score === 'number' ? input.actualMetrics.score : null;
  if (predictedScore != null && actualScore != null && predictedScore > 0) {
    const ratio = actualScore / predictedScore;
    // Clamp to [0, 2] then normalize so 1.0 = perfect match.
    scoreAccuracy = Math.max(0, Math.min(2, ratio));
  }

  const { data, error } = await supabase
    .from('item_deliveries')
    .insert({
      item_id: input.itemId,
      workspace_id: input.workspaceId,
      delivery_type: input.deliveryType,
      predicted_scores: input.predictedScores ?? null,
      actual_metrics: input.actualMetrics ?? null,
      score_accuracy: scoreAccuracy,
      notes: input.notes ?? null,
      lessons_learned: input.lessonsLearned ?? null,
      created_by: input.createdBy,
    })
    .select()
    .single();

  if (error) throw new Error(`Failed to create delivery record: ${error.message}`);
  // The item's history says what was delivered (2026-10-15; this left no trace).
  void import('../stores/prioritization/itemsStore')
    .then(({ recordItemFieldChange }) => recordItemFieldChange(input.itemId, 'delivery', null, input.deliveryType))
    .catch(() => {});
  return rowToDelivery(data);
}

export async function getDeliveriesForItem(itemId: string): Promise<ItemDelivery[]> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('item_deliveries')
    .select('*')
    .eq('item_id', itemId)
    .order('delivered_at', { ascending: false });

  if (error) throw new Error(`Failed to load deliveries: ${error.message}`);
  return (data ?? []).map(rowToDelivery);
}

export async function getAllDeliveriesForWorkspace(workspaceId: string): Promise<ItemDelivery[]> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('item_deliveries')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('delivered_at', { ascending: false });

  if (error) throw new Error(`Failed to load workspace deliveries: ${error.message}`);
  return (data ?? []).map(rowToDelivery);
}

// The stats rule lives in one place, shared with the public API
// (GET /v1/calibration): supabase/functions/_shared/calibrationCore.ts.
export { computeCalibrationStats };
